// -----------------------------------------------------------------------------
// State management hook for NeatoLabs
// Version: 0.0.1
// 21 September 2023
// -----------------------------------------------------------------------------

// Import libraries
import {ReactNode, useState} from 'react'
import {closeNotif, startNotif, updateNotif} from '@/components/DynamicForm/util/notifier'
import Number from '@/components/DynamicForm/elements/Number'

// Types
type Callback = (data: any, stateData: any, defaultValue?: any) => void
type Message = (data: any, stateData: any, defaultValue?: any) => string | ReactNode

type StepType = {
    main: (stateData: any, defaultData: any, updateNotification: any) => Promise<any>
    error?: Callback
    success?: Callback
    startMessage?: Message
    errorMessage?: Message
    successMessage?: Message
}

type Steps = Record<string, StepType>

// Hook
const useStateManagement = (steps: Steps) => {
    // State to store data from each step
    const [stateData, setStateData] = useState({})

    const [stateInProgress, setStateInProgress] = useState(false)

    // Notification ID for start message
    let notifId: any = null

    // Run all steps in order
    const runSteps = async (defaultValue?: any): Promise<void> => {
        setStateInProgress(true)
        const entries = Object.entries(steps)
        const len = entries.length

        const updateNotification = (updateText: string | ReactNode, index: any, total: any) => {
            if (notifId && updateText) {
                const progress = index !== null ? index / total : undefined
                updateNotif(notifId, updateText, progress)
            }
        }
        let innerState: any = {}
        for (let i = 0; i < len; i++) {
            const [stateName, {main, error, success, errorMessage, successMessage, startMessage}] = entries[i]
            const lastItem = i === len - 1

            // -----------------------------------------------------------------
            // Run main function of each step
            // -----------------------------------------------------------------
            try {
                // Start Notification for each step if startMessage is provided
                if (startMessage) {
                    if (!notifId) {
                        notifId = startNotif(startMessage(null, innerState, defaultValue))
                    } else {
                        updateNotification(startMessage(null, innerState, defaultValue), i, len)
                        //updateNotif(notifId, startMessage(null, stateData, defaultValue))
                    }
                }

                // Run main function of each step

                //console.log(`NeatoLabs state management. "${stateName}" last state data:`, innerState)
                const result = await main(innerState, defaultValue, (text: string) => updateNotification(text, i, len))
                innerState[stateName] = result
                // Set stateData with result

                setStateData(async (prevStateData) => {
                    return {...prevStateData, [stateName]: result}
                })

                // Call success callback if provided

                if (success) {
                    success(result, innerState, defaultValue)
                }

                // Update or close notification if successMessage is provided
                if (successMessage) {
                    if (lastItem && notifId) {
                        closeNotif(notifId, successMessage(result, innerState, defaultValue))
                        notifId = null
                    }
                    if (!lastItem && !notifId) {
                        notifId = startNotif('...')
                        closeNotif(notifId, successMessage(result, innerState, defaultValue))
                    }
                    if (!lastItem && notifId) {
                        updateNotification(successMessage(result, innerState, defaultValue), i, len)
                        //updateNotif(notifId, successMessage(result, stateData, defaultValue))
                    }

                    // Close notification if successMessage is not provided, and we have notification in progress
                } else if (lastItem && notifId) {
                    closeNotif(notifId, 'Done and Success!')
                    notifId = null
                }
                if (lastItem) {
                    setStateInProgress(false)
                }

                // -----------------------------------------------------------------
                // Error handling
                // -----------------------------------------------------------------
            } catch (e) {
                console.error(`NeatoLabs state management. Failed at state: ${stateName}`, e)

                // Call error callback if provided
                if (error) {
                    error(e, stateData, defaultValue)
                }

                // Close notification if errorMessage is provided
                if (errorMessage) {
                    closeNotif(notifId, errorMessage(e, stateData, defaultValue), {type: 'error'})
                    notifId = null

                    // Close notification if errorMessage is not provided, and we have notification in progress
                } else if (notifId) {
                    closeNotif(notifId, 'Operation Failed!', {type: 'error'})
                    notifId = null
                }
                setStateInProgress(false)
                return
            }
        }
        //console.log('NeatoLabs state management. Process completed successfully')
    }
    return {runSteps, stateData, stateInProgress}
}

export default useStateManagement
